import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isNull from 'lodash/isNull';

import { lazyGettext } from '@eb/lazy-gettext';
import { Button } from '@eb/eds-button';
import { Icon } from '@eb/eds-icon';
import { LogoApple } from '@eb/eds-iconography';

import { SIZE_BLOCK } from '@eb/eds-button';

import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE } from './constants';

import { buildAppleLoginResponse } from './utils';

import './AppleLoginButton.scss';

export default class AppleLoginButton extends Component {
    static propTypes = {
        onClick: PropTypes.func.isRequired,
        /**
         * Enabling siwa in popup.
         * Right now only in the new authentication flow that is not tested entirely.
         */
        shouldUseApplePopup: PropTypes.bool,
    };

    componentDidMount() {
        ((doc, id, appleInit) => {
            if (isNull(doc.getElementById(id))) {
                // if the script tag is not present
                const scriptTag = doc.createElement('script');
                scriptTag.id = id;
                scriptTag.type = 'text/javascript';
                scriptTag.src =
                    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
                document.body.append(scriptTag);

                /**
                 * It is possible that the SDK is still not loaded when calling this function
                 * so we wait for it to load before calling it.
                 */
                scriptTag.onload = () => {
                    if (appleInit) {
                        appleInit();
                    }
                };
            } else if (appleInit) {
                appleInit();
            }
        })(document, 'apple-id-auth', this.appleInit);
    }

    appleInit = () => {
        const {
            clientId,
            scope,
            redirectURI,
            shouldUseApplePopup,
        } = this.props;

        if (this.areParametersValid()) {
            AppleID.auth.init({
                clientId,
                scope,
                redirectURI,
                usePopup: shouldUseApplePopup,
            });
        }
    };

    handleClick = () => {
        const {
            onClick,
            onSocialConnectVerified,
            onSocialConnectNotAuthorized,
            shouldUseApplePopup,
        } = this.props;

        onClick(SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE);

        if (shouldUseApplePopup) {
            AppleID.auth
                .signIn()
                .then((response) => {
                    /**
                     * response: {object}
                     * code: A single-use authentication code that is valid for five minutes.
                     * id_token: A JSON web token containing the user’s identify information.
                     * state: The state passed by the init function.
                     * user: A JSON string containing the data requested in the scope property. ONLY RETURNED THE FIRST TIME
                     * * { "name": { "firstName": string, "lastName": string }, "email": string }
                     * * for further uses of this the id_token will have to be decoded
                     *  {
                            "authorization": {
                                "state": "[STATE]",
                                "code": "[CODE]",
                                "id_token": "[ID_TOKEN]"
                            },
                            "user": {
                                "email": "[EMAIL]",
                                "name": {
                                    "firstName": "[FIRST_NAME]",
                                    "lastName": "[LAST_NAME]"
                                }
                            }
                        }
                     */
                    const parsedResponse = buildAppleLoginResponse(response);

                    if (onSocialConnectVerified) {
                        onSocialConnectVerified(parsedResponse);
                    }
                })
                .catch((status) => {
                    /**
                     * error: The returned error code. (currently only: `user_cancelled_authorize`)
                     * state: The state passed by the init function.
                     *  {
                           "error": "[ERROR_CODE]"
                        }
                     */
                    if (onSocialConnectNotAuthorized) {
                        onSocialConnectNotAuthorized({
                            sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
                            status,
                        });
                    }
                });
        } else {
            AppleID.auth.signIn();
        }
    };

    areParametersValid = () => {
        const { clientId, redirectURI } = this.props;

        return clientId && redirectURI;
    };

    render() {
        return (
            <div className="apple-login__container">
                <Button
                    type="button"
                    size={SIZE_BLOCK}
                    onClick={this.handleClick}
                    disabled={!this.areParametersValid()}
                >
                    <div className="apple-login__icon">
                        <Icon type={<LogoApple />} size="small" color="white" />
                    </div>
                    <div className="apple-login__text">
                        {lazyGettext('Continue with Apple')}
                    </div>
                </Button>
            </div>
        );
    }
}
